// @ts-check

const DEFAULT_CONCURRENT_UPLOADS = 3;
const DEFAULT_MAX_CONNECTIONS = 5;

class UploadScheduler {
  /**
   * Creates a scheduler with separate limits for active transfers and open requests.
   *
   * @param {import('./upload-scheduler.types').UploadSchedulerOptions} [options]
   */
  constructor(options = {}) {
    const { concurrentUploads = DEFAULT_CONCURRENT_UPLOADS, maxConnections = DEFAULT_MAX_CONNECTIONS } = options;

    this.concurrentUploads = Math.min(concurrentUploads, maxConnections);
    this.maxConnections = maxConnections;

    /** @type {import('./upload-scheduler.types').UploadQueueItem[]} */
    this.queue = [];

    /** @type {Map<import('./upload-scheduler.types').UploadId, import('./upload-scheduler.types').ActiveUploadRequest>} */
    this.activeRequests = new Map();

    this.activeUploads = 0;
    this.activeConnections = 0;
  }

  /**
   * Adds an upload request to the queue.
   *
   * @param {import('./upload-scheduler.types').UploadId} id - Unique identifier of the upload.
   * @param {() => Promise<unknown>} start - Function that starts the upload request.
   * @returns {Promise<unknown>} Promise that settles with the upload request.
   */
  enqueue(id, start) {
    return new Promise((resolve, reject) => {
      this.queue.push({ id, start, resolve, reject });
      this._drainQueue();
    });
  }

  /**
   * Releases a transfer slot while keeping the request connection open.
   *
   * @param {import('./upload-scheduler.types').UploadId} id - Identifier of the completed transfer.
   * @returns {void}
   */
  markUploadComplete(id) {
    const request = this.activeRequests.get(id);
    if (!request || request.uploadComplete) return;

    request.uploadComplete = true;
    this.activeUploads -= 1;
    this._drainQueue();
  }

  /** @returns {void} */
  _drainQueue() {
    while (
      this.queue.length &&
      this.activeUploads < this.concurrentUploads &&
      this.activeConnections < this.maxConnections
    ) {
      const queueItem = this.queue.shift();
      if (!queueItem) return;

      this.activeUploads += 1;
      this.activeConnections += 1;
      this.activeRequests.set(queueItem.id, { uploadComplete: false });

      Promise.resolve()
        .then(queueItem.start)
        .then(
          (result) => {
            this._finishRequest(queueItem.id);
            queueItem.resolve(result);
          },
          (error) => {
            this._finishRequest(queueItem.id);
            queueItem.reject(error);
          },
        );
    }
  }

  /**
   * Releases the connection slot after the server responds or the request fails.
   *
   * @param {import('./upload-scheduler.types').UploadId} id - Identifier of the finished request.
   * @returns {void}
   */
  _finishRequest(id) {
    const request = this.activeRequests.get(id);
    if (!request) return;

    if (!request.uploadComplete) this.activeUploads -= 1;
    this.activeConnections -= 1;
    this.activeRequests.delete(id);
    this._drainQueue();
  }
}

export default UploadScheduler;
